import React, { useContext } from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import ExposeCard from "@/components/Landingpage/ExposeCard";
import { FavoritesContext } from "@/lib/FavoritesContext";
import { motion } from "framer-motion";
import { IconPin } from "@tabler/icons-react";

export default function Map({ className, estates, onEstateClick }) {
  const { favorites, handleFavoriteToggle } = useContext(FavoritesContext);

  // Custom price marker with hover effect
// inside your component file
const createCustomMarker = (price) => {
  const formattedPrice = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(price);

  return L.divIcon({
    html: `
      <div style="
        width: 60px; height: 60px;
        overflow: hidden; border-radius: 50%;
        opacity: 0.9;
        display:flex; align-items:center; justify-content:center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg"  xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="60px" height="60px" viewBox="0 0 256 256" xml:space="preserve">
<g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(45.02412451361867 45.024124513618645) scale(1.83 1.83)">
	<path d="M 45 0 C 27.395 0 13.123 14.272 13.123 31.877 c 0 7.86 2.858 15.043 7.573 20.6 L 45 81.101 l 24.304 -28.624 c 4.716 -5.558 7.573 -12.741 7.573 -20.6 C 76.877 14.272 62.605 0 45 0 z M 45 43.889 c -7.24 0 -13.11 -5.869 -13.11 -13.11 c 0 -7.24 5.869 -13.11 13.11 -13.11 s 13.11 5.869 13.11 13.11 C 58.11 38.02 52.24 43.889 45 43.889 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: #2d4a7b; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"/>
	<path d="M 58.958 71.559 L 45 82.839 L 31.057 71.556 c -9.329 1.65 -15.682 4.901 -15.682 8.645 c 0 5.412 13.263 9.8 29.625 9.8 c 16.361 0 29.625 -4.388 29.625 -9.8 C 74.625 76.458 68.278 73.209 58.958 71.559 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: #; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"/>
</g>
</svg>
      </div>
    `,
    className: "",
    iconSize: [25, 25],
    iconAnchor: [20, 25],
    popupAnchor: [4, -25],
  });
};

  // Custom cluster styling
  const createClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    return L.divIcon({
      html: `
        <div style="
          display: flex; align-items: center; justify-content: center;
          width: 42px; height: 42px; border-radius: 50%;
          background: #e95c0c; border: 2px solid #e95c0c; opacity: 0.9;
          color: white; font-weight: bold; font-size: 14px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        ">
          ${count}
        </div>
      `,
      className: "",
      iconSize: [42, 42],
    });
  };

  return (
    <div className={className}>
      <MapContainer
        center={[50.8107, 6.2631]}
        zoom={10}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://tile.jawg.io/dfbe15c6-374e-4f04-92c0-5c9678cd7132/{z}/{x}/{y}{r}.png?access-token=aIs4QlE6wRbH35AB9YspWkO82STp4zrWRwcuEkmwWFgJ4WhsEqThj2fsGhCT7g7w"
          attribution='<a href="https://www.jawg.io?utm_medium=map&utm_source=attribution" target="_blank">&copy; Jawg</a> - <a href="https://www.openstreetmap.org?utm_medium=map-attribution&utm_source=jawg" target="_blank">&copy; OpenStreetMap</a> contributors'
        />
        <MarkerClusterGroup
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={true}
          iconCreateFunction={createClusterIcon}
          maxClusterRadius={40}
        >
          {estates
            .filter((estate) => estate.lat && estate.lng)
            .map((estate) => (
              <Marker
                key={estate.id}
                position={[estate.lat, estate.lng]}
                icon={createCustomMarker(estate.price || estate.base_rent)}
                eventHandlers={{
                  click: () => {
                    e.target.openPopup();
                  },
                  mouseover: (e) => {
                    e.target.openPopup();
                  },
                }}
              >
             
                  <Popup>
                    <ExposeCard
                      id={estate.id}
                      image={estate.images?.[0]?.original || "/Haus.jpeg"}
                      price={estate.price}
                      location={`${estate.zip_code}, ${estate.city}`}
                      details={`${estate.number_of_rooms} Zimmer, ${estate.living_space} m²`}
                      description={estate.title}
                      onClick={() => onEstateClick(estate.id)}
                      className="h-fit text-prime-900 rounded-xl overflow-hidden text-[110%] shadow-md transition-all bg-white border-none"
                      isFavorited={favorites.some((fav) => fav.id === estate.id)}
                      onFavoriteToggle={() => handleFavoriteToggle(estate)}
                      base_rent={estate.base_rent}
                      marketing_type={estate.marketing_type}
                    />
                  </Popup>
              </Marker>
            ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
}
